/**
 * PeriodDetailView
 * Per-follower eligibility breakdown for a specific payout period
 */

import { GlowStatCard } from '@components/ui/GlowStatCard';
import type { PeriodDetail } from '@hooks/useCreatorEarnings';
import { INELIGIBILITY_LABELS } from '@parlay-picker/shared/creatorPayout';
import { formatCents, formatPeriodLabel } from '@parlay-picker/shared/formatting';
import { cn } from '@utils/cn';
import { AlertTriangle, Check, ChevronLeft, X } from 'lucide-react';
import type { ReactElement } from 'react';

interface PeriodDetailViewProps {
  periodDetail: PeriodDetail;
  loading: boolean;
  onBack: () => void;
}

export function PeriodDetailView({
  periodDetail,
  loading,
  onBack,
}: PeriodDetailViewProps): ReactElement {
  const { payout, followers } = periodDetail;

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-theme-border rounded w-48" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-24 bg-theme-border rounded-xl" />
          ))}
        </div>
        <div className="h-48 bg-theme-border rounded" />
      </div>
    );
  }

  return (
    <div>
      {/* Back button + header */}
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-theme-text-secondary hover:text-theme-text transition-colors mb-4"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Overview
      </button>

      <h3 className="text-lg font-semibold text-theme-text mb-4">
        {formatPeriodLabel(payout.periodLabel)}
      </h3>

      {/* Period stats */}
      <div className="grid grid-cols-2 @3xl:grid-cols-4 gap-3 mb-6">
        <GlowStatCard label="Your Earnings" accent="success">
          <span className="text-theme-success text-lg">
            {formatCents(payout.creatorShareCents)}
          </span>
        </GlowStatCard>
        <GlowStatCard
          label="Eligible"
          sub={`of ${payout.totalFollowers} followers`}
          accent="default"
        >
          <span className="text-lg">{payout.eligibleFollowers}</span>
        </GlowStatCard>
        <GlowStatCard label="Gross Revenue" accent="default">
          <span className="text-lg">{formatCents(payout.grossRevenueCents)}</span>
        </GlowStatCard>
        <GlowStatCard label="Stripe Fees" accent="default">
          <span className="text-lg">{formatCents(payout.stripeFeesCents)}</span>
        </GlowStatCard>
      </div>

      {/* Follower table */}
      <div className="bg-theme-surface/50 rounded-lg overflow-hidden border border-theme-border">
        <div className="px-4 py-3 border-b border-theme-border">
          <h4 className="text-sm font-semibold text-theme-text">Follower Eligibility</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-theme-surface border-b border-theme-border">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                  Follower
                </th>
                <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                  Followed Since
                </th>
                <th className="hidden sm:table-cell px-4 py-3 text-right text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                  Days
                </th>
                <th className="hidden sm:table-cell px-4 py-3 text-center text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                  Sportsbook
                </th>
                <th className="hidden sm:table-cell px-4 py-3 text-right text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                  Wagered
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-tertiary uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-theme-border">
              {followers.map((follower) => (
                <tr key={follower.followerId}>
                  <td className="px-4 py-3 text-sm font-medium text-theme-text">
                    {follower.displayName}
                  </td>
                  <td className="hidden sm:table-cell px-4 py-3 text-sm text-theme-text-secondary">
                    {new Date(follower.followedAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </td>
                  <td className="hidden sm:table-cell px-4 py-3 text-sm text-theme-text-secondary text-right">
                    {follower.followDurationDays}d
                  </td>
                  <td className="hidden sm:table-cell px-4 py-3 text-center">
                    {follower.hasSportsbookLinked ? (
                      <Check className="w-4 h-4 text-theme-success inline" />
                    ) : (
                      <X className="w-4 h-4 text-theme-danger inline" />
                    )}
                  </td>
                  <td className="hidden sm:table-cell px-4 py-3 text-sm text-theme-text-secondary text-right">
                    {formatCents(follower.wageredCents)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {follower.isEligible ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-theme-success">
                        <Check className="w-3.5 h-3.5" />
                        Eligible
                      </span>
                    ) : (
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 text-xs font-medium text-theme-warning',
                        )}
                      >
                        <AlertTriangle className="w-3.5 h-3.5" />
                        {INELIGIBILITY_LABELS[
                          follower.ineligibilityReason as keyof typeof INELIGIBILITY_LABELS
                        ] ?? 'Ineligible'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
